import React, { useState, useCallback, useEffect } from "react";
import { DialogTitle } from "@mui/material";
import ManagersModalForm from "../../components/managers/ManagersModalForm";
import ManagersModel, { ManagerData, defaultManagerData } from "../../core/api/manager/ManagersModel";
import { confirmDialog } from "../../components/common/ConfirmDialog";
import { useNotifications } from "../../hooks/NotificationProvider";
import { 
  ContentContainer, 
  StyledModal 
} from "./styles/ManagersListStyles";
import ManagersTable from "../../components/managers/ManagersTable";
import { translations } from "../../translations/common";

export const Managers: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<ManagerData>(defaultManagerData);
  const [managers, setManagers] = useState<ManagerData[]>([]);
  const notifications = useNotifications();

  const loadManagers = useCallback(async () => {
    try {
      const data = await ManagersModel.listAll();
      setManagers(data);
    } catch (error) {
      notifications.notify({
        title: translations.managers.notifications.deleteError.title,
        message: translations.managers.notifications.deleteError.message,
        level: "error"
      });
    } finally {
    }
  }, [notifications]);

  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  const handleNewManager = useCallback(() => {
    setSelectedManager(defaultManagerData);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    loadManagers();
  }, [loadManagers]);

  const handleEditManager = useCallback((manager: ManagerData) => {
    setSelectedManager(manager);
    setIsModalOpen(true);
  }, []);

  const handleDeleteManager = useCallback((manager: ManagerData) => {
    confirmDialog({
      action: async () => {
        try {
          await ManagersModel.delete(manager.id || '');
          notifications.notify({
            title: translations.managers.notifications.deleteSuccess.title,
            message: translations.managers.notifications.deleteSuccess.message,
            level: "success",
          });
          loadManagers();
        } catch (error) {
          console.error("Failed to delete manager:", error);
          notifications.notify({
            title: translations.managers.notifications.deleteError.title,
            message: translations.managers.notifications.deleteError.message,
            level: "error",
          });
        }
      },
      actionName: translations.managers.confirmations.deleteAction,
      body: translations.managers.confirmations.deleteBody(manager.firstName, manager.lastName),
      title: translations.managers.confirmations.deleteTitle,
    });
  }, [notifications, loadManagers]);

  return (
    <ContentContainer
      container
      alignItems="flex-start"
      justifyContent="flex-end"
      direction="row"
      spacing={3}
    >
        <ManagersTable
          managers={managers}
          onEdit={handleEditManager}
          onDelete={handleDeleteManager}
          onAddNew={handleNewManager}
        />
      <StyledModal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{translations.managers.form.title}</DialogTitle>
        <ManagersModalForm formValues={selectedManager} onSuccess={handleCloseModal} />
      </StyledModal>
    </ContentContainer>
  );
};

export default Managers;